import {AnimatePresence, motion, useReducedMotion} from 'framer-motion';
import {Button, Card, Tooltip} from 'platform/components';
import {Box, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {type ReactNode, useId, useState} from 'react';

import i18n from '@dms/i18n';

import {suffixTestId, type TestIdProps} from 'shared/web';

import type {WorkshopDashboardFilters} from '../types/WorkshopDashboardFilters';

interface WorkshopOverviewCardProps extends TestIdProps {
  filters: WorkshopDashboardFilters;
  renderFilters: () => ReactNode;
  children: ReactNode;
}

const FILTER_TRANSITION = {duration: 0.2, ease: 'easeInOut'} as const;
const INSTANT_TRANSITION = {duration: 0};
const COLLAPSED_FILTERS = {height: 0, opacity: 0, overflow: 'hidden'};
const EXPANDED_FILTERS = {height: 'auto', opacity: 1, overflow: 'hidden'};

export function WorkshopOverviewCard(props: WorkshopOverviewCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const panelId = useId();
  const shouldReduceMotion = useReducedMotion();
  const activeFilterCount = Object.values(props.filters).filter(
    (values) => values.length > 0
  ).length;
  const filterLabel = i18n.t('page.datagrid.labels.filters');

  return (
    <Card
      title={i18n.t('page.homepage.workshopOverview.title')}
      hasSeparator={false}
      isFullHeight
      actions={[
        {
          type: 'custom',
          component: (
            <Tooltip label={filterLabel} placement="bottom">
              <Button
                aria-label={filterLabel}
                leftIcon="content/filter_list"
                variant="link"
                iconColor="text.primary"
                badge={
                  activeFilterCount > 0
                    ? {value: activeFilterCount.toString(), colorScheme: 'blue'}
                    : undefined
                }
                aria-expanded={isExpanded}
                aria-controls={panelId}
                onClick={() => setIsExpanded((current) => !current)}
                data-testid={suffixTestId('workshopOverviewFilterToggle', props)}
              />
            </Tooltip>
          ),
        },
      ]}
      data-testid={suffixTestId('workshopOverview', props)}
    >
      <Box height="100%" minHeight={0} overflowY="auto">
        <VStack>
          <AnimatePresence initial={false}>
            {match(isExpanded)
              .with(true, () => (
                <motion.section
                  key={panelId}
                  id={panelId}
                  role="region"
                  aria-label={filterLabel}
                  initial={COLLAPSED_FILTERS}
                  animate={EXPANDED_FILTERS}
                  exit={COLLAPSED_FILTERS}
                  transition={shouldReduceMotion ? INSTANT_TRANSITION : FILTER_TRANSITION}
                >
                  <Box paddingBottom={4}>{props.renderFilters()}</Box>
                </motion.section>
              ))
              .otherwise(() => null)}
          </AnimatePresence>
          {props.children}
        </VStack>
      </Box>
    </Card>
  );
}
